import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { UUID } from '@benzinga/session';
import { Session } from '@benzinga/session';
import {
  CalendarType,
  CalendarCallType,
  CalendarEvents,
  CalendarManagerEvent,
  CalendarDataParameters,
  ErrorType,
  CalendarFeedId,
  CalendarDataParams,
  CalendarTypeEvents,
  CompanyEventsQueryParams,
  RatingsAnalystsParams,
  AnalystData,
  CompanyEventDetails,
  SEC,
  SecCalendarParams,
} from './entities';
import { EventsAction } from './generateManagerParams';
import { CALENDAR_PAGESIZE } from './generateRestfulParams';
import { CalendarDataRestful, CalendarsEventsRestful } from './restful';

export class CalendarRequest extends Subscribable<CalendarManagerEvent> {
  private eventsRestful: CalendarsEventsRestful;
  private dataRestful: CalendarDataRestful;
  private feedId: CalendarFeedId;
  private session: Session;

  constructor(session: Session, feedId: CalendarFeedId) {
    super();
    this.eventsRestful = new CalendarsEventsRestful(session);
    this.dataRestful = new CalendarDataRestful(session);
    this.session = session;
    this.feedId = feedId;
  }

  public updateEvents = async (parameters: CalendarDataParameters): Promise<void> => {
    const newEvents = await this.eventsRestful.fetchEvents(parameters, this.session, CALENDAR_PAGESIZE);
    if (newEvents.err) {
      this.dispatch({
        error: newEvents.err,
        errorType: ErrorType.UPDATE_EVENTS,
        feedId: this.feedId,
        type: CalendarCallType.ERROR,
      });
    } else if (newEvents.ok.length) {
      this.dispatch({
        feedId: this.feedId,
        newEvents: newEvents.ok,
        parameters,
        type: CalendarCallType.UPDATE_EVENTS,
      });
    }
  };

  public loadEvents = async (
    requestParameters: CalendarDataParameters | undefined,
    parameters: CalendarDataParameters,
    session: Session,
    eventsAction: EventsAction,
    sagaId: UUID = '',
    pagesize: number = CALENDAR_PAGESIZE,
  ): Promise<void> => {
    let error!: SafeError;
    if (requestParameters) {
      const newEvents = await this.eventsRestful.fetchEvents(requestParameters, session, pagesize);
      if (newEvents.err) {
        error = newEvents.err;
      } else {
        this.dispatch({
          eventsAction,
          feedId: this.feedId,
          newEvents: newEvents.ok,
          parameters,
          requestParameters,
          sagaId,
          type: CalendarCallType.SET_EVENTS,
        });
      }
    } else {
      error = new SafeError('No parameters found', 'undefined_parameters');
    }
    if (error) {
      this.dispatch({
        error,
        errorType: ErrorType.GET_EVENTS,
        feedId: this.feedId,
        parameters,
        sagaId,
        type: CalendarCallType.ERROR,
      });
    }
  };

  public amendEvents = (
    requestParameters: CalendarDataParameters | undefined,
    parameters: CalendarDataParameters,
    _prevEvents: CalendarEvents[] | undefined,
    eventsAction: EventsAction,
    sagaId: UUID,
  ): void => {
    if (parameters) {
      this.dispatch({
        eventsAction,
        feedId: this.feedId,
        newEvents: [],
        parameters,
        requestParameters: requestParameters ?? parameters,
        sagaId,
        type: CalendarCallType.SET_EVENTS,
      });
    }
  };

  public setParameters = (parameters: CalendarDataParameters | undefined, sagaId: UUID): void => {
    if (parameters) {
      this.dispatch({
        feedId: this.feedId,
        parameters,
        sagaId,
        type: CalendarCallType.SET_PARAMETERS,
      });
    }
  };

  public isUsingProxy = (): boolean => this.dataRestful.isUsingProxy();

  public fetchCalendarData = async <T extends CalendarType>(
    calendarType: CalendarType,
    params: CalendarDataParams,
  ): SafePromise<Required<CalendarTypeEvents>[T]> => {
    const calendarDataResponse = await this.dataRestful.fetchCalendarData(calendarType, params);

    if (calendarDataResponse.err) {
      this.dispatch({
        calendarType,
        error: calendarDataResponse.err,
        errorType: ErrorType.GET_DATA,
        feedId: this.feedId,
        type: CalendarCallType.ERROR,
      });

      return { err: calendarDataResponse.err };
    } else {
      const raw = calendarDataResponse?.ok;
      const rawObject = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
      const result =
        (Array.isArray(raw)
          ? raw
          : (rawObject?.[calendarType] ??
            (calendarType === 'optionsActivity' ? rawObject?.option_activity : undefined))) ?? [];
      const list = Array.isArray(result) ? result : [];

      this.dispatch({
        calendarType,
        feedId: this.feedId,
        result: list,
        type: CalendarCallType.GET_CALENDAR_DATA,
      });

      return { ok: list as Required<CalendarTypeEvents>[T] };
    }
  };

  public getRatingsAnalysts = async (query: RatingsAnalystsParams): SafePromise<AnalystData | null> => {
    const response = await this.dataRestful.getRatingsAnalysts(query);

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: ErrorType.GET_DATA,
        feedId: this.feedId,
        type: CalendarCallType.ERROR,
      });

      return { err: response.err };
    } else {
      const ok: AnalystData | null = Array.isArray(response?.ok?.analyst_ratings_analyst)
        ? response?.ok?.analyst_ratings_analyst[0]
        : null;

      this.dispatch({
        result: ok,
        type: 'get_analyst_data',
      });

      return { ok };
    }
  };

  public getCompanyEvents = async (query: CompanyEventsQueryParams): SafePromise<CompanyEventDetails[] | null> => {
    const response = await this.dataRestful.getCompanyEvents(query);

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: ErrorType.GET_DATA,
        feedId: this.feedId,
        type: CalendarCallType.ERROR,
      });

      return { err: response.err };
    } else {
      this.dispatch({
        result: response?.ok?.events ?? null,
        type: 'get_company_events_data',
      });

      return { ok: response?.ok?.events ?? null };
    }
  };

  public getSecCalendarData = async (query: SecCalendarParams): SafePromise<SEC[]> => {
    const response = await this.dataRestful.getSecCalendarData(query);

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: ErrorType.GET_DATA,
        feedId: this.feedId,
        type: CalendarCallType.ERROR,
      });

      return { err: response.err };
    } else {
      this.dispatch({
        result: response?.ok?.sec ?? [],
        type: 'get_sec_calendar_data',
      });
      return { ok: response?.ok?.sec ?? [] };
    }
  };
}
